import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import UploadIcon from '@atlaskit/icon/core/upload';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button';

import { useCfind5926UploadInOnboarding } from '../hooks/useCfind5926UploadInOnboarding.ts';

const i18n = defineMessages({
	importLabel: {
		id: 'experiment-cfind-5926-upload-in-onboarding.import-cta.label',
		defaultMessage: 'Import',
		description: 'Label for the Import CTA button shown in the left nav during onboarding',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IconWrapper = styled.span({
	color: token('color.icon.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TitleWrapper = styled.span({
	display: 'flex',
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	marginLeft: token('space.050'),
});

/**
 * "Import" CTA rendered in the space side navigation for new users.
 *
 * This component is 100% gated behind the cfind-5926-upload-in-onboarding experiment.
 * It renders nothing when:
 * - The experiment is loading
 * - The experiment is off (control arm)
 * - The user is not eligible (not founding admin, or outside first 2 weeks)
 *
 * Styled to match the TreeCreateContentButton ("+ Create" button) in the page tree.
 */
export const ImportOnboardingCTA = ({ onClick }: { onClick: () => void }) => {
	const { isEnabled, loading } = useCfind5926UploadInOnboarding();
	const intl = useIntl();

	if (loading || !isEnabled) {
		return null;
	}

	const icon = (
		<IconWrapper>
			<UploadIcon label="" size="small"/>
		</IconWrapper>
	);

	return (
		<Button
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				height: '36px',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				paddingLeft: '7px',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				marginLeft: '0px',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				alignItems: 'center',
			}}
			onClick={onClick}
			testId="import-onboarding-cta"
			iconBefore={icon}
			appearance="subtle"
			shouldFitContainer
		>
			<TitleWrapper>{intl.formatMessage(i18n.importLabel)}</TitleWrapper>
		</Button>
	);
};
